/**
 * =============================================================================
 * background-video.js — We Are Rewind
 * =============================================================================
 * Chargement des vidéos d'ambiance rendues par snippets/background-video.liquid
 * (<video data-background-video>, sans source au rendu).
 *
 *   - Rien n'est téléchargé tant que la vidéo n'approche pas de l'écran : une
 *     vidéo masquée (ex. footer en mobile, display:none) n'est jamais chargée.
 *   - L'affiche (poster) est posée dès l'approche ; la vidéo elle-même attend en
 *     plus l'événement load, pour ne pas concurrencer l'image du hero (LCP).
 *   - Résolution selon la largeur d'écran : 480p < 768 px ≤ 720p < 1280 px ≤ 1080p
 *     (480p si le visiteur a activé l'économie de données).
 *   - Lecture quand la vidéo est à l'écran, pause quand elle en sort.
 *
 * Liquid ne donne que le MP4 1080p (data-src) et la playlist HLS (data-hls).
 * Shopify génère aussi des MP4 480p / 720p, nommés comme les variantes de la
 * playlist : « <id>.SD-480p.1.5Mbps.hls.<n>_.m3u8 » → « <id>.SD-480p-1.5Mbps-<n>.mp4 ».
 * On lit donc la playlist pour trouver le bon fichier. En cas d'échec (playlist
 * illisible, nommage différent, fichier introuvable), on lit le 1080p.
 *
 * Les sections rechargées dans l'éditeur de thème sont prises en charge.
 * =============================================================================
 */
(function () {
  'use strict';

  const SELECTOR = 'video[data-background-video]';
  const MARGIN = '300px 0px';
  const VARIANT = /^(.+)\.((?:SD|HD)-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/;

  function targetHeight() {
    const saveData = navigator.connection && navigator.connection.saveData;
    if (saveData || window.innerWidth < 768) return 480;
    if (window.innerWidth < 1280) return 720;
    return 1080;
  }

  /* MP4 de la variante la plus haute ≤ cible (à défaut, la plus basse). */
  async function resolveSource(video) {
    const fallback = video.dataset.src;
    const target = targetHeight();
    if (!video.dataset.hls || target >= 1080) return fallback;
    try {
      const playlist = new URL(video.dataset.hls, location.href);
      const response = await fetch(playlist);
      if (!response.ok) return fallback;
      const lines = (await response.text()).split('\n').map((line) => line.trim());
      let best = null;
      lines.forEach((line, i) => {
        const resolution = /RESOLUTION=\d+x(\d+)/.exec(line);
        const name = resolution && VARIANT.exec(lines[i + 1] || '');
        if (!name) return;
        const height = Number(resolution[1]);
        const better = !best
          || (height <= target && (best.height > target || height > best.height))
          || (height > target && best.height > target && height < best.height);
        if (better) best = { height, name };
      });
      if (!best) return fallback;
      const [, base, quality, bitrate, id] = best.name;
      const mp4 = new URL(`${base}.${quality}-${bitrate}-${id}.mp4`, playlist);
      if (fallback) mp4.search = new URL(fallback, location.href).search;
      return mp4.href;
    } catch (error) {
      return fallback;
    }
  }

  const sources = new WeakMap();

  function play(video) {
    if (!sources.has(video)) {
      sources.set(video, resolveSource(video).then((src) => {
        if (!src) return;
        video.muted = true;
        video.addEventListener('error', () => {
          const fallback = video.dataset.src;
          if (fallback && video.currentSrc !== new URL(fallback, location.href).href) {
            video.src = fallback;
            if (video.dataset.visible === 'true') video.play().catch(() => {});
          }
        }, { once: true });
        video.src = src;
      }));
    }
    sources.get(video).then(() => {
      if (video.src && video.dataset.visible === 'true') video.play().catch(() => {});
    });
  }

  let pageLoaded = document.readyState === 'complete';
  const waiting = new Set();

  function onIntersect(entries) {
    entries.forEach((entry) => {
      const video = entry.target;
      video.dataset.visible = String(entry.isIntersecting);
      if (entry.isIntersecting) {
        if (video.dataset.poster && !video.poster) video.poster = video.dataset.poster;
        if (pageLoaded) play(video);
        else waiting.add(video);
      } else {
        waiting.delete(video);
        if (!video.paused) video.pause();
      }
    });
  }

  if (!window.IntersectionObserver) {
    window.addEventListener('load', () => {
      document.querySelectorAll(SELECTOR).forEach((video) => {
        video.dataset.visible = 'true';
        play(video);
      });
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersect, { rootMargin: MARGIN });

  function observe(scope) {
    scope.querySelectorAll(SELECTOR).forEach((video) => observer.observe(video));
  }

  if (!pageLoaded) {
    window.addEventListener('load', () => {
      pageLoaded = true;
      waiting.forEach(play);
      waiting.clear();
    });
  }

  observe(document);
  document.addEventListener('shopify:section:load', (event) => observe(event.target));
})();
